import { Button, Modal } from '@payaca/ambrosia';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import { FunctionComponent, useCallback, useState, type JSX } from 'react';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

import { archiveTimelogType } from '@payaca/store/timelogs/timelogsActions';
import { PublicTimelogType } from '@payaca/types/timelogs';
import { useDispatch } from 'react-redux';
import CreateEditTimelogTypeControl from '../createEditTimelogTypeControl/CreateEditTimelogTypeControl';
import './ManageableTimelogType.sass';

type Props = {
  timelogType: PublicTimelogType;
  isReadOnly?: boolean;
  onUpdateSuccess?: () => void;
  onArchiveSuccess?: () => void;
  isArchivable?: boolean;
};

const ManageableTimelogType: FunctionComponent<Props> = ({
  timelogType,
  isReadOnly,
  onUpdateSuccess,
  onArchiveSuccess,
  isArchivable,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmArchiveModal, setShowConfirmArchiveModal] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const archive = useCallback(() => {
    setIsArchiving(true);
    dispatch(
      archiveTimelogType.request({
        timelogTypePublicId: timelogType.publicId,
        callback: () => {
          setIsArchiving(false);
          setShowConfirmArchiveModal(false);
          onArchiveSuccess?.();
        },
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [timelogType.publicId, onArchiveSuccess]);

  return (
    <div className="manageable-timelog-type">
      {!isEditing && (
        <div className="read-view">
          <span className="type">{timelogType.type}</span>
          <span className="availability">
            {timelogType.isAvailableForGeneralTimelogs
              ? 'Events and general timelogs'
              : 'Events only'}
          </span>
          {!!timelogType.description?.length && (
            <span className="description">{timelogType.description}</span>
          )}
          {!isReadOnly && (
            <PermissionGuard
              renderIfHasPermissions={[
                TimelogsPermissions.MANAGE_TIMELOG_TYPES,
              ]}
            >
              <div className="actions">
                <Button variant="link" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
                {isArchivable && (
                  <Button
                    variant="link"
                    onClick={() => setShowConfirmArchiveModal(true)}
                  >
                    Archive
                  </Button>
                )}
              </div>
            </PermissionGuard>
          )}
        </div>
      )}
      {isEditing && (
        <CreateEditTimelogTypeControl
          timelogType={timelogType}
          onCreateEditSuccess={() => {
            onUpdateSuccess?.();
            setIsEditing(false);
          }}
        />
      )}
      <Modal
        title="Archive timelog type"
        isOpen={showConfirmArchiveModal}
        onClose={() => setShowConfirmArchiveModal(false)}
      >
        <Modal.Body>
          <p>Are you sure you want to archive this timelog type?</p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              size="sm"
              onClick={archive}
              isProcessing={isArchiving}
              replaceContentWhenProcessing
            >
              Archive
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageableTimelogType;
